import { AppText } from "@toothfairy/shared-ui";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppTextChip from "@toothfairy/shared-ui/AppTextChip";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import authorisationsHelper from "../../../API/Authorisations/authorisationsHelper";
import workspacesHelper from "../../../API/Workspaces/workspacesHelper";
import Constants from "../../../Constants";
import useWorkspace from "../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../MainViewContainer";

const WebhookSettingsView = () => {
  const { getActiveWorkspace } = useWorkspace();
  const { currentTheme } = AppTheme.useTheme();
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const activeWorkspace = getActiveWorkspace();
  const formRef = useRef(null);
  const [availableAuthorisations, setAvailableAuthorisations] = useState([]);

  const validationSchema = AppForms.yup.object().shape({
    webhookUrl: AppForms.yup.string().url().nullable(),
    webhookAuthorisationType: AppForms.yup.string().nullable(),
    webhookAuthorisationID: AppForms.yup.string().nullable(),
  });

  const fetchAuthorisations = async () => {
    const authorisations = await authorisationsHelper.getAuthorisations(
      activeWorkspace?.id,
    );
    const _auth =
      authorisations?.data?.authorisationsByWorkspace?.items?.filter(
        (u) => u?._deleted != true,
      ) || [];
    setAvailableAuthorisations(_auth);
  };

  useEffect(() => {
    if (activeWorkspace?.id) {
      fetchAuthorisations();
    }
  }, [activeWorkspace?.id]);

  const handleSaveWebhook = useCallback(
    async (values, { setSubmitting }) => {
      if (!activeWorkspace?.id) return;

      try {
        await workspacesHelper.updateWorkspace(activeWorkspace.id, [
          { property: "webhookUrl", value: values.webhookUrl || null },
          {
            property: "webhookAuthorisationType",
            value: values.webhookAuthorisationType?.toLowerCase() || null,
          },
          {
            property: "webhookAuthorisationID",
            value:
              values.webhookAuthorisationType !== "NONE" &&
              values.webhookAuthorisationType !== "none" &&
              values.webhookAuthorisationType !== null
                ? values.webhookAuthorisationID || null
                : null,
          },
        ]);
        AppToasty.show(t("admin.webhook.savedCaption"), { type: "success" });
      } catch (error) {
        console.error("Error saving webhook URL:", error);
        AppToasty.show(t("admin.webhook.saveErrorCaption"), { type: "danger" });
      } finally {
        setSubmitting(false);
      }
    },
    [activeWorkspace?.id, AppToasty, t],
  );

  const initialValues = {
    webhookUrl: activeWorkspace?.webhookUrl || "",
    webhookAuthorisationType:
      activeWorkspace?.webhookAuthorisationType?.toUpperCase() || "NONE",
    webhookAuthorisationID: activeWorkspace?.webhookAuthorisationID || "none",
  };

  const WebhookSettingsFormContent = () => {
    const { values } = AppForms.useFormikContext();

    return (
      <View style={{ width: "100%", alignItems: "center" }}>
        <AppForms.AppFormField
          showTextInputLabel
          wrapperInputStyle={{
            paddingTop: 5,
          }}
          textContainer={{
            width: Math.min(500, 500),
            backgroundColor: currentTheme?.theme?.lightGrey,
            marginVertical: 10,
          }}
          style={{
            backgroundColor: currentTheme?.theme?.lightGrey,
          }}
          label={t("admin.webhook.urlLabel")}
          placeholder={t("admin.webhook.urlPlaceholder")}
          keyboardtype="url"
          textContextType="label"
          autoCorrect={false}
          autoCapitalize="none"
          name="webhookUrl"
        />
        <AppForms.AppFormDropDown
          isElevated={false}
          dropDownStyle={{
            borderColor: currentTheme?.theme?.border_color,
            maxWidth: !isLargeScreen() && 250,
          }}
          formFieldContainerStyle={{
            width: "100%",
          }}
          showTextInputLabel
          label={t("admin.webhook.authTypeLabel")}
          name="webhookAuthorisationType"
          items={["BEARER", "APIKEY", "NONE"].map((item) => ({
            id: item,
            name: item,
          }))}
          propertyLabel="name"
          propertyValue="id"
        />
        {values?.webhookAuthorisationType !== "NONE" &&
          values?.webhookAuthorisationType !== "none" &&
          values?.webhookAuthorisationType !== null && (
            <AppForms.AppFormDropDown
              isElevated={false}
              showTextInputLabel
              formFieldContainerStyle={{
                width: "100%",
              }}
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              items={[
                { id: "none", name: "none" },
                ...availableAuthorisations?.filter(
                  (u) =>
                    u?.type === values?.webhookAuthorisationType?.toLowerCase(),
                ),
              ]}
              label={t("admin.webhook.authLabel")}
              placeholder={t("admin.webhook.authPlaceholder")}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="webhookAuthorisationID"
              hideShowIcon={true}
              propertyLabel="name"
              propertyValue="id"
            />
          )}
        <AppForms.AppSubmitButton
          title={t("admin.webhook.saveButton")}
          awesomeIconColor={currentTheme?.theme?.white}
          backgroundColor={currentTheme?.theme?.primary}
          btnContentStyle={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
          style={[
            {
              height: Constants.appButtonHeight,
              width: "auto",
              shadowColor: currentTheme?.theme?.transparent,
              marginHorizontal: 10,
            },
          ]}
          wrapperStyle={[
            {
              width: "fit-content",
              minWidth: 150,
              marginTop: 10,
              alignSelf: "center",
              height: Constants.appButtonHeight,
            },
          ]}
        />
      </View>
    );
  };

  return (
    <View
      style={{
        marginHorizontal: "auto",
        paddingHorizontal: 20,
        paddingVertical: 10,
        height: 300,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <View
        style={{
          flexDirection: "column",
          alignItems: "center",
          alignSelf: "flex-start",
          width: "100%",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            alignSelf: "flex-start",
            marginTop: 20,
          }}
        >
          <AppText fontWeight="bold" color={currentTheme?.theme?.font_color}>
            {t("admin.webhook.title")}
          </AppText>
          <AppTextChip
            mainContainerStyle={{
              marginLeft: 8,
            }}
            styleLabel={{
              fontSize: 10,
              fontWeight: "600",
            }}
            color={currentTheme?.theme?.orange}
            label={t("admin.webhook.betaLabel")}
          />
        </View>
        <AppText
          wrapperStyle={{
            alignSelf: "center",
            marginTop: 10,
          }}
          numberOfLines={3}
        >
          {t("admin.webhook.sectionCaption")}
        </AppText>
        <AppText
          color={currentTheme?.theme?.darkGrey}
          numberOfLines={3}
          wrapperStyle={{
            marginVertical: 10,
          }}
          style={{
            fontStyle: "italic",
          }}
        >
          {t("admin.webhook.sectionSubCaption")}
        </AppText>
      </View>

      <AppForms.AppForm
        ref={formRef}
        initialValues={initialValues}
        onSubmit={handleSaveWebhook}
        validationSchema={validationSchema}
        enableReinitialize
      >
        <WebhookSettingsFormContent />
      </AppForms.AppForm>
    </View>
  );
};

export default WebhookSettingsView;
